// @ts-nocheck

import { type EntitiesCategories } from '../states/entities/categories';
import { type CategoryState } from '../states/entities/categories';
import { type EntitiesMatches } from '../states/entities/matches';
import { type MatchId } from '../states/entities/matches';
import { type MatchState } from '../states/entities/matches';
import { type EntitiesSports } from '../states/entities/sports';
import { type SportState } from '../states/entities/sports';
import { type EntitiesTournaments } from '../states/entities/tournaments';
import { type TournamentState } from '../states/entities/tournaments';
import { type StateType } from '../states/index';
import { type Selector, type SelectorMaker } from '../types/selectors';
import { createResultEqualSelector } from '../utils/reselect';

import { selectEntitiesCalendarEntityMatches } from './entities/calendar/entitiesCalendarPrimary.selector';
import { selectEntitiesCategories } from './entities/categories/entitiesCategoriesPrimary.selector';
import { type EntitiesCategoriesSelectorProps } from './entities/categories/entitiesCategoriesPrimary.selector';
import { selectEntitiesFavoritesMatches } from './entities/favorites/entitiesFavoritesPrimary.selector';
import { selectEntitiesHomeMatches } from './entities/home/entitiesHomePrimary.selector';
import { selectEntitiesLiveMatches } from './entities/live/entitiesLivePrimary.selector';
import { selectEntitiesMatches } from './entities/matches/entitiesMatchesPrimary.selector';
import { selectEntitiesSports } from './entities/sports/entitiesSportsPrimary.selector';
import { type EntitiesSportsSelectorProps } from './entities/sports/entitiesSportsPrimary.selector';
import { selectEntitiesTournaments } from './entities/tournaments/entitiesTournamentsPrimary.selector';
import { type EntitiesTournamentsSelectorProps } from './entities/tournaments/entitiesTournamentsPrimary.selector';

const emptyArray: [] = [];

// no-memoize
export const selectCatalogEntities: Selector<SportState | CategoryState | TournamentState | null> = (
  state: StateType,
  props?: EntitiesSportsSelectorProps & EntitiesCategoriesSelectorProps & EntitiesTournamentsSelectorProps,
) => {
  const sportsEntities = selectEntitiesSports(state);
  const categoriesEntities = selectEntitiesCategories(state);
  const tournamentsEntities = selectEntitiesTournaments(state);

  const { sportId, categoryId, tournamentId } = props || {};

  if (tournamentId !== undefined) {
    return tournamentsEntities[tournamentId];
  }
  if (categoryId !== undefined) {
    return categoriesEntities[categoryId];
  }
  if (sportId !== undefined) {
    return sportsEntities[sportId];
  }

  return null;
};

const getMatches = (
  matchIds: Array<MatchId> | null,
  matches: EntitiesMatches,
  sports: EntitiesSports,
  categories: EntitiesCategories,
  tournaments: EntitiesTournaments,
): Array<MatchState> => {
  if (matchIds) {
    const result = [];

    matchIds.forEach((matchId) => {
      const match = matches[matchId];

      if (match !== undefined && match !== null) {
        const { sportId, tournamentId, categoryId } = match;
        const sport = sports[sportId];

        if (sport !== undefined && sport !== null) {
          const category = categories[categoryId];
          if (category !== undefined && category !== null) {
            const tournament = tournaments[tournamentId];
            if (tournament !== undefined && tournament !== null) {
              result.push({
                ...match,
                sportName: sport.sportName,
                categoryName: category.categoryName,
                flag: tournament.flag || category.flag,
                tournamentName: tournament.tournamentName,
                topName: tournament.topName,
              });
            }
          }
        }
      }
    });

    if (result.length > 0) {
      return result;
    }
  }
  return emptyArray;
};

export const selectHomeMatchesEntities: Selector<Array<MatchState>> = createResultEqualSelector(
  [
    selectEntitiesHomeMatches,
    selectEntitiesMatches,
    selectEntitiesSports,
    selectEntitiesCategories,
    selectEntitiesTournaments,
  ],
  (entitiesHomeMatches, matches, sports, categories, tournaments) =>
    getMatches(entitiesHomeMatches, matches, sports, categories, tournaments),
);

export const selectCalendarMatchesEntities: Selector<Array<MatchState>> = createResultEqualSelector(
  [
    selectEntitiesCalendarEntityMatches,
    selectEntitiesMatches,
    selectEntitiesSports,
    selectEntitiesCategories,
    selectEntitiesTournaments,
  ],
  (entitiesCalendarMatches, matches, sports, categories, tournaments) =>
    getMatches(entitiesCalendarMatches, matches, sports, categories, tournaments),
);

export const selectLiveMatchesEntities: Selector<Array<MatchState>> = createResultEqualSelector(
  [
    selectEntitiesLiveMatches,
    selectEntitiesMatches,
    selectEntitiesSports,
    selectEntitiesCategories,
    selectEntitiesTournaments,
  ],
  (entitiesLiveMatches, matches, sports, categories, tournaments) =>
    getMatches(entitiesLiveMatches, matches, sports, categories, tournaments),
);

export const selectFavoritesMatchesEntities: Selector<Array<MatchState>> = createResultEqualSelector(
  [
    selectEntitiesFavoritesMatches,
    selectEntitiesMatches,
    selectEntitiesSports,
    selectEntitiesCategories,
    selectEntitiesTournaments,
  ],
  (entitiesFavoritesMatches, matches, sports, categories, tournaments) =>
    getMatches(entitiesFavoritesMatches, matches, sports, categories, tournaments),
);

export const makeLocatedCatalogEntities: SelectorMaker<Array<MatchState>> = () =>
  createResultEqualSelector(
    [
      selectCatalogEntities,
      selectEntitiesMatches,
      selectEntitiesSports,
      selectEntitiesCategories,
      selectEntitiesTournaments,
    ],
    (entitiesCatalog, matches, sports, categories, tournaments) =>
      getMatches(entitiesCatalog?.matches, matches, sports, categories, tournaments),
  );
